import { useEffect, useRef, useState } from 'react';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Laptop, Plus, Download, Search, Filter, X, ArrowLeftRight, RotateCcw, Trash2, Boxes, CheckCircle2, AlertTriangle, Wrench, FileBarChart, FileSpreadsheet, FileText, Users, Pencil } from 'lucide-react';
import { toast } from 'sonner';

const CATEGORIES = ['Laptops', 'Desktops', 'Monitors', 'Mobile Phones', 'Tablets',
  'Access Cards', 'SIM Cards', 'Security Tokens', 'Headsets', 'Furniture', 'Other'];
const STATUSES = ['available', 'assigned', 'repair', 'lost', 'damaged', 'retired', 'disposed'];

const STATUS_STYLES = {
  available: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  assigned:  'bg-blue-50    text-blue-700    border-blue-200',
  repair:    'bg-amber-50   text-amber-700   border-amber-200',
  lost:      'bg-rose-50    text-rose-700    border-rose-200',
  damaged:   'bg-rose-50    text-rose-700    border-rose-200',
  retired:   'bg-slate-100  text-slate-600   border-slate-200',
  disposed:  'bg-slate-100  text-slate-600   border-slate-200',
};

const Kpi = ({ label, value, tone = 'slate', testid }) => (
  <div className="bg-white border border-slate-200 rounded-2xl p-4" data-testid={testid}>
    <div className={`text-[10px] font-bold uppercase tracking-[0.18em] text-${tone}-600`}>{label}</div>
    <div className="font-heading text-3xl font-bold mt-1 text-slate-900">{value}</div>
  </div>
);

export default function Assets() {
  const { user } = useAuth();
  const canManage = ['admin', 'hr', 'it_admin'].some(r => (user?.roles || [user?.role]).includes(r));
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [createOpen, setCreateOpen] = useState(false);
  const [reportsOpen, setReportsOpen] = useState(false);
  const [assignFor, setAssignFor] = useState(null); // asset to assign
  const [transferFor, setTransferFor] = useState(null); // asset to transfer
  const [returnFor, setReturnFor] = useState(null); // asset to return
  const [editFor, setEditFor] = useState(null); // asset being edited

  const changeStatus = async (asset, newStatus) => {
    if (newStatus === asset.status) return;
    if (!window.confirm(`Change ${asset.asset_id} status from "${asset.status}" → "${newStatus}"?`)) return;
    try {
      await api.patch(`/assets/${asset.id}`, { status: newStatus });
      toast.success(`Status updated → ${newStatus}`);
      setReload(r => r + 1);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Status update failed'); }
  };
  const deleteAsset = async (asset) => {
    if (!window.confirm(`Permanently delete ${asset.asset_id} (${asset.name})? This cannot be undone.`)) return;
    try {
      await api.delete(`/assets/${asset.id}`);
      toast.success(`Deleted ${asset.asset_id}`);
      setReload(r => r + 1);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Delete failed'); }
  };
  const [reload, setReload] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const params = new URLSearchParams();
        if (filterStatus !== 'all') params.append('status', filterStatus);
        if (filterCategory !== 'all') params.append('category', filterCategory);
        if (q) params.append('q', q);
        const [s, l] = await Promise.all([
          api.get('/assets/summary'),
          api.get(`/assets?${params.toString()}`),
        ]);
        if (cancelled) return;
        setSummary(s.data);
        setRows(l.data);
      } catch (e) { toast.error('Could not load assets'); }
    })();
    return () => { cancelled = true; };
  }, [q, filterStatus, filterCategory, reload]);

  // Build a native href for the inventory CSV with current filters embedded.
  // Native anchor downloads bypass async-blob-click which some browsers block.
  const csvHref = (() => {
    const token = localStorage.getItem('hrms_token') || '';
    const base = process.env.REACT_APP_BACKEND_URL || '';
    const params = new URLSearchParams();
    if (filterStatus !== 'all') params.append('status', filterStatus);
    if (filterCategory !== 'all') params.append('category', filterCategory);
    params.append('auth', token);
    return `${base}/api/assets.csv?${params.toString()}`;
  })();

  return (
    <div className="space-y-6" data-testid="assets-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.25em] text-slate-500">Inventory</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 mt-1">Assets & Inventory</h1>
          <p className="text-slate-500 mt-1">Track every laptop, SIM card, access card and headset — and who holds it.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={() => setReportsOpen(true)} className="rounded-full h-10" data-testid="open-reports-btn">
            <FileBarChart className="w-4 h-4 mr-2" /> Reports
          </Button>
          <a href={csvHref}
             download={`assets-${new Date().toISOString().slice(0, 10)}.csv`}
             className="inline-flex items-center justify-center rounded-full h-10 px-4 text-sm font-medium border border-slate-200 hover:bg-slate-50"
             data-testid="export-assets-csv">
            <Download className="w-4 h-4 mr-2" /> Export CSV
          </a>
          {canManage && (
            <Button onClick={() => setCreateOpen(true)} className="rounded-full h-10 bg-[#3D4A8E] hover:bg-[#2F3A6E]" data-testid="create-asset-button">
              <Plus className="w-4 h-4 mr-1.5" /> Add asset
            </Button>
          )}
        </div>
      </div>

      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          <Kpi label="Total" value={summary.total} testid="kpi-total" />
          <Kpi label="Assigned" value={summary.by_status.assigned} tone="blue" testid="kpi-assigned" />
          <Kpi label="Available" value={summary.by_status.available} tone="emerald" testid="kpi-available" />
          <Kpi label="In Repair" value={summary.by_status.repair} tone="amber" testid="kpi-repair" />
          <Kpi label="Lost" value={summary.by_status.lost} tone="rose" testid="kpi-lost" />
          <Kpi label="Due return" value={summary.due_for_return} tone="orange" testid="kpi-due" />
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-2xl p-4" data-testid="asset-filters">
        <div className="flex items-center gap-3 mb-3 pb-3 border-b border-slate-100">
          <Search className="w-4 h-4 text-slate-400" />
          <input data-testid="asset-search" placeholder="Search by name, serial, brand, model…"
                 value={q} onChange={(e) => setQ(e.target.value)}
                 className="flex-1 outline-none bg-transparent text-sm" />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 inline-flex items-center gap-1.5">
            <Filter className="w-3.5 h-3.5" /> Filters
          </span>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="h-9 rounded-full text-xs px-3 w-auto min-w-[140px]" data-testid="filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Any status</SelectItem>
              {STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterCategory} onValueChange={setFilterCategory}>
            <SelectTrigger className="h-9 rounded-full text-xs px-3 w-auto min-w-[150px]" data-testid="filter-category"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Any category</SelectItem>
              {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          {(filterStatus !== 'all' || filterCategory !== 'all') && (
            <button onClick={() => { setFilterStatus('all'); setFilterCategory('all'); }}
                    className="text-xs text-slate-500 hover:text-rose-600 inline-flex items-center gap-1 px-2 py-1 rounded-full">
              <X className="w-3 h-3" /> Clear
            </button>
          )}
          <span className="ml-auto text-xs text-slate-500"><b className="text-slate-900">{rows.length}</b> asset{rows.length !== 1 ? 's' : ''}</span>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid="asset-table">
        <div className="grid grid-cols-12 gap-2 text-[10px] uppercase tracking-[0.15em] font-bold text-slate-500 bg-slate-50 px-4 py-3 border-b border-slate-200">
          <div className="col-span-2">Asset ID</div>
          <div className="col-span-3">Name</div>
          <div className="col-span-2">Category</div>
          <div className="col-span-2">Status</div>
          <div className="col-span-2">Assigned to</div>
          <div className="col-span-1 text-right">Actions</div>
        </div>
        <div className="max-h-[60vh] overflow-y-auto">
          {rows.length === 0 && <div className="text-center py-12 text-slate-400 text-sm">No assets match the current filters.</div>}
          {rows.map(a => (
            <div key={a.id} data-testid={`asset-row-${a.id}`}
                 className="grid grid-cols-12 gap-2 text-xs px-4 py-2.5 border-b border-slate-100 hover:bg-slate-50">
              <div className="col-span-2 font-mono text-[11px] text-slate-700">{a.asset_id}</div>
              <div className="col-span-3 min-w-0">
                <div className="font-semibold text-slate-900 truncate">{a.name}</div>
                <div className="text-[10px] text-slate-500 truncate">{a.brand} {a.model} {a.serial_number && `· ${a.serial_number}`}</div>
              </div>
              <div className="col-span-2 text-slate-600">{a.category}</div>
              <div className="col-span-2">
                <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full border ${STATUS_STYLES[a.status] || ''}`}>
                  {a.status}
                </span>
              </div>
              <div className="col-span-2 text-slate-700 truncate">{a.assigned_to_name || '—'}</div>
              <div className="col-span-1 flex justify-end gap-1.5">
                {canManage && a.status === 'available' && (
                  <button onClick={() => setAssignFor(a)} data-testid={`assign-${a.id}`}
                          title="Assign to employee"
                          className="text-blue-600 hover:bg-blue-50 rounded-full p-1.5">
                    <ArrowLeftRight className="w-3.5 h-3.5" />
                  </button>
                )}
                {canManage && a.status === 'assigned' && (
                  <>
                    <button onClick={() => setTransferFor(a)} data-testid={`transfer-${a.id}`}
                            title="Transfer to another employee"
                            className="text-indigo-600 hover:bg-indigo-50 rounded-full p-1.5">
                      <Users className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => setReturnFor(a)} data-testid={`return-${a.id}`}
                            title="Mark return"
                            className="text-amber-600 hover:bg-amber-50 rounded-full p-1.5">
                      <RotateCcw className="w-3.5 h-3.5" />
                    </button>
                  </>
                )}
                {canManage && (
                  <>
                    <button onClick={() => setEditFor(a)} data-testid={`edit-${a.id}`}
                            title="Edit asset details"
                            className="text-slate-600 hover:bg-slate-100 rounded-full p-1.5">
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    <select value={a.status}
                            onChange={(e) => changeStatus(a, e.target.value)}
                            data-testid={`status-${a.id}`}
                            title="Change status"
                            onClick={(e) => e.stopPropagation()}
                            className="text-[10px] border border-slate-200 rounded-full px-1.5 py-1 hover:border-slate-400 bg-white cursor-pointer">
                      <option value="available">available</option>
                      <option value="assigned" disabled>assigned</option>
                      <option value="repair">repair</option>
                      <option value="retired">retired</option>
                      <option value="lost">lost</option>
                      <option value="damaged">damaged</option>
                      <option value="disposed">disposed</option>
                    </select>
                    {a.status !== 'assigned' && (
                      <button onClick={() => deleteAsset(a)} data-testid={`delete-${a.id}`}
                              title="Delete asset (irreversible)"
                              className="text-rose-600 hover:bg-rose-50 rounded-full p-1.5">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      <CreateAssetDialog open={createOpen} onOpenChange={setCreateOpen}
                         onSaved={() => { setCreateOpen(false); setReload(r => r + 1); }} />
      <EditAssetDialog key={`edit-${editFor?.id || 'none'}`} asset={editFor}
                       onClose={() => setEditFor(null)}
                       onSaved={() => { setEditFor(null); setReload(r => r + 1); }} />
      <ReportsDialog open={reportsOpen} onClose={() => setReportsOpen(false)} />
      <AssignDialog key={`assign-${assignFor?.id || 'none'}`} asset={assignFor} onClose={() => setAssignFor(null)}
                    onDone={() => { setAssignFor(null); setReload(r => r + 1); }} />
      <TransferDialog key={`transfer-${transferFor?.id || 'none'}`} asset={transferFor} onClose={() => setTransferFor(null)}
                      onDone={() => { setTransferFor(null); setReload(r => r + 1); }} />
      <ReturnDialog key={`return-${returnFor?.id || 'none'}`} asset={returnFor} onClose={() => setReturnFor(null)}
                    onDone={() => { setReturnFor(null); setReload(r => r + 1); }} />
    </div>
  );
}

function CreateAssetDialog({ open, onOpenChange, onSaved }) {
  const [form, setForm] = useState({ name: '', category: 'Laptops', brand: '', model: '', serial_number: '', purchase_cost: '', vendor: '', warranty_expiry: '', notes: '' });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    setBusy(true);
    try {
      const body = { ...form, purchase_cost: form.purchase_cost ? Number(form.purchase_cost) : null };
      const { data } = await api.post('/assets', body);
      toast.success(`Asset ${data.asset_id} created`);
      setForm({ name: '', category: 'Laptops', brand: '', model: '', serial_number: '', purchase_cost: '', vendor: '', warranty_expiry: '', notes: '' });
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="create-asset-dialog" className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">Add new asset</DialogTitle>
          <DialogDescription>An Asset ID will be generated automatically.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><label className="text-xs font-semibold text-slate-600">Name *</label><Input data-testid="asset-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-xl mt-1" /></div>
          <div><label className="text-xs font-semibold text-slate-600">Category *</label>
            <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
              <SelectTrigger className="rounded-xl mt-1" data-testid="asset-category"><SelectValue /></SelectTrigger>
              <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><label className="text-xs font-semibold text-slate-600">Brand</label><Input value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} className="rounded-xl mt-1" /></div>
          <div><label className="text-xs font-semibold text-slate-600">Model</label><Input value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} className="rounded-xl mt-1" /></div>
          <div><label className="text-xs font-semibold text-slate-600">Serial number</label><Input value={form.serial_number} onChange={(e) => setForm({ ...form, serial_number: e.target.value })} className="rounded-xl mt-1" /></div>
          <div><label className="text-xs font-semibold text-slate-600">Purchase cost (₹)</label><Input type="number" value={form.purchase_cost} onChange={(e) => setForm({ ...form, purchase_cost: e.target.value })} className="rounded-xl mt-1" /></div>
          <div><label className="text-xs font-semibold text-slate-600">Vendor</label><Input value={form.vendor} onChange={(e) => setForm({ ...form, vendor: e.target.value })} className="rounded-xl mt-1" /></div>
          <div><label className="text-xs font-semibold text-slate-600">Warranty expiry</label><Input type="date" value={form.warranty_expiry} onChange={(e) => setForm({ ...form, warranty_expiry: e.target.value })} className="rounded-xl mt-1" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy} className="rounded-full">Cancel</Button>
          <Button onClick={save} disabled={busy} className="rounded-full bg-[#3D4A8E] hover:bg-[#2F3A6E]" data-testid="save-asset">{busy ? 'Saving…' : 'Create asset'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ---------- Edit asset dialog ---------- */
function EditAssetDialog({ asset, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: asset?.name || '', category: asset?.category || 'Laptops',
    brand: asset?.brand || '', model: asset?.model || '',
    serial_number: asset?.serial_number || '',
    purchase_cost: asset?.purchase_cost || '',
    vendor: asset?.vendor || '',
    warranty_expiry: asset?.warranty_expiry || '',
    notes: asset?.notes || '',
  });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    setBusy(true);
    try {
      const payload = { ...form };
      if (payload.purchase_cost === '') delete payload.purchase_cost;
      else payload.purchase_cost = parseFloat(payload.purchase_cost);
      await api.patch(`/assets/${asset.id}`, payload);
      toast.success(`Updated ${asset.asset_id}`);
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Update failed'); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={!!asset} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="edit-asset-dialog" className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">Edit asset</DialogTitle>
          <DialogDescription><span className="font-mono">{asset?.asset_id}</span> · status {asset?.status}</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><label className="text-xs font-semibold text-slate-600">Name *</label><Input data-testid="edit-asset-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-xl mt-1" /></div>
          <div>
            <label className="text-xs font-semibold text-slate-600">Category *</label>
            <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
              <SelectTrigger className="rounded-xl mt-1" data-testid="edit-asset-category"><SelectValue/></SelectTrigger>
              <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><label className="text-xs font-semibold text-slate-600">Brand</label><Input value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} className="rounded-xl mt-1" /></div>
          <div><label className="text-xs font-semibold text-slate-600">Model</label><Input value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} className="rounded-xl mt-1" /></div>
          <div><label className="text-xs font-semibold text-slate-600">Serial number</label><Input value={form.serial_number} onChange={(e) => setForm({ ...form, serial_number: e.target.value })} className="rounded-xl mt-1" /></div>
          <div><label className="text-xs font-semibold text-slate-600">Purchase cost (₹)</label><Input type="number" value={form.purchase_cost} onChange={(e) => setForm({ ...form, purchase_cost: e.target.value })} className="rounded-xl mt-1" /></div>
          <div><label className="text-xs font-semibold text-slate-600">Vendor</label><Input value={form.vendor} onChange={(e) => setForm({ ...form, vendor: e.target.value })} className="rounded-xl mt-1" /></div>
          <div><label className="text-xs font-semibold text-slate-600">Warranty expiry</label><Input type="date" value={form.warranty_expiry || ''} onChange={(e) => setForm({ ...form, warranty_expiry: e.target.value })} className="rounded-xl mt-1" /></div>
          <div className="col-span-2"><label className="text-xs font-semibold text-slate-600">Notes</label><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rounded-xl mt-1" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-full">Cancel</Button>
          <Button onClick={save} disabled={busy} className="rounded-full bg-[#3D4A8E] hover:bg-[#2F3A6E]" data-testid="confirm-edit-asset">
            {busy ? 'Saving…' : 'Save changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}



function AssignDialog({ asset, onClose, onDone }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState(null);
  const [untilOffboarding, setUntilOffboarding] = useState(true);
  const [returnDate, setReturnDate] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const debounceRef = useRef(null);
  const abortRef = useRef(null);

  const runSearch = (q) => {
    setQuery(q);
    setSelected(null);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (abortRef.current) abortRef.current.abort();
    if (q.trim().length < 2) { setResults([]); setSearching(false); return; }
    setSearching(true);
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/assets/employee-search?q=${encodeURIComponent(q.trim())}`, { signal: ctrl.signal });
        setResults(data || []);
      } catch (e) { /* ignore aborts */ }
      finally { setSearching(false); }
    }, 250);
  };

  const save = async () => {
    if (!selected) { toast.error('Pick an employee'); return; }
    setBusy(true);
    try {
      await api.post(`/assets/${asset.id}/assign`, {
        employee_id: selected.id,
        until_offboarding: untilOffboarding,
        expected_return_date: untilOffboarding ? null : (returnDate || null),
        notes,
      });
      toast.success(`Asset assigned to ${selected.name}`);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Assign failed'); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={!!asset} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="assign-asset-dialog" className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">Assign {asset?.name}</DialogTitle>
          <DialogDescription>{asset?.asset_id} · {asset?.category}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold text-slate-600">Search employee by name or Employee ID *</label>
            <Input value={query} onChange={(e) => runSearch(e.target.value)}
                   placeholder="e.g. Arjun, LAN-2026-0001…"
                   className="rounded-xl mt-1" data-testid="assign-employee-search" />
            {searching && <div className="text-xs text-slate-500 mt-1">Searching…</div>}
            {!selected && results.length > 0 && (
              <div className="border border-slate-200 rounded-xl mt-2 max-h-64 overflow-y-auto" data-testid="assign-search-results">
                {results.map(e => (
                  <button key={e.id}
                          onClick={() => { setSelected(e); setResults([]); setQuery(''); }}
                          className="w-full text-left px-3 py-2 border-b border-slate-100 last:border-0 hover:bg-slate-50"
                          data-testid={`assign-result-${e.id}`}>
                    <div className="font-semibold text-slate-900 text-sm">{e.name}</div>
                    <div className="text-[11px] text-slate-500">
                      {e.employee_id && <span className="font-mono">{e.employee_id}</span>}
                      {e.employee_id && (e.department || e.title) && <> · </>}
                      {e.department}{e.title && <> · {e.title}</>}
                      {e.email && <> · {e.email}</>}
                    </div>
                  </button>
                ))}
              </div>
            )}
            {!selected && !searching && query.trim().length >= 2 && results.length === 0 && (
              <div className="text-xs text-slate-500 mt-1">No matching employees.</div>
            )}
          </div>

          {selected && (
            <div className="border border-emerald-200 bg-emerald-50 rounded-xl p-3 flex items-start gap-3" data-testid="assign-selected-employee">
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-slate-900 text-sm">{selected.name}</div>
                <div className="text-[11px] text-slate-600">
                  {selected.employee_id && <span className="font-mono">{selected.employee_id}</span>}
                  {selected.department && <> · {selected.department}</>}
                  {selected.email && <> · {selected.email}</>}
                </div>
              </div>
              <button onClick={() => setSelected(null)} className="text-slate-500 hover:text-rose-600" data-testid="clear-selected-employee">
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          {selected && (
            <div className="border border-slate-200 rounded-xl p-3">
              <div className="text-xs font-semibold text-slate-600 mb-2">Return policy</div>
              <label className="flex items-center gap-2 cursor-pointer mb-2">
                <input type="radio" checked={untilOffboarding} onChange={() => setUntilOffboarding(true)} data-testid="assign-policy-until-offboarding" />
                <span className="text-sm">Until employee resigns or is terminated</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="radio" checked={!untilOffboarding} onChange={() => setUntilOffboarding(false)} data-testid="assign-policy-fixed-date" />
                <span className="text-sm">Fixed return date</span>
              </label>
              {!untilOffboarding && (
                <Input type="date" value={returnDate} onChange={(e) => setReturnDate(e.target.value)}
                       className="rounded-xl mt-2" data-testid="assign-fixed-return-date" />
              )}
            </div>
          )}

          {selected && (
            <div>
              <label className="text-xs font-semibold text-slate-600">Notes</label>
              <Input value={notes} onChange={(e) => setNotes(e.target.value)} className="rounded-xl mt-1" />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-full">Cancel</Button>
          <Button onClick={save} disabled={busy || !selected || (!untilOffboarding && !returnDate)} className="rounded-full bg-[#3D4A8E] hover:bg-[#2F3A6E]" data-testid="confirm-assign-asset">
            {busy ? 'Assigning…' : 'Assign'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReturnDialog({ asset, onClose, onDone }) {
  const [form, setForm] = useState({ return_status: 'returned', notes: '' });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.post(`/assets/${asset.id}/return`, form);
      toast.success('Return recorded');
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Return failed'); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={!!asset} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="return-asset-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">Return {asset?.name}</DialogTitle>
          <DialogDescription>Currently with {asset?.assigned_to_name}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold text-slate-600">Return status *</label>
            <Select value={form.return_status} onValueChange={(v) => setForm({ ...form, return_status: v })}>
              <SelectTrigger className="rounded-xl mt-1" data-testid="return-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="returned">Returned (back to available)</SelectItem>
                <SelectItem value="missing">Missing — write off as lost</SelectItem>
                <SelectItem value="damaged">Damaged</SelectItem>
                <SelectItem value="replacement_required">Damaged — replacement required</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div><label className="text-xs font-semibold text-slate-600">Notes</label><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rounded-xl mt-1" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-full">Cancel</Button>
          <Button onClick={save} disabled={busy} className="rounded-full bg-[#3D4A8E] hover:bg-[#2F3A6E]" data-testid="confirm-return-asset">{busy ? 'Saving…' : 'Record return'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/* ---------- Reports dialog ---------- */
function ReportsDialog({ open, onClose }) {
  const [reports, setReports] = useState([]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get('/assets/reports/list');
        if (!cancelled) setReports(data || []);
      } catch (e) { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [open]);

  // Build a native `<a href>` link with the auth token embedded as ?auth=…
  // This bypasses async XHR-then-blob-then-click — which some browsers
  // (Brave, Safari, in-app webviews) treat as a popup and silently block.
  const buildUrl = (kind, format) => {
    const token = localStorage.getItem('hrms_token') || '';
    const base = process.env.REACT_APP_BACKEND_URL || '';
    return `${base}/api/assets/reports/${kind}?format=${format}&auth=${encodeURIComponent(token)}`;
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="asset-reports-dialog" className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">Asset Reports</DialogTitle>
          <DialogDescription>Download structured reports as CSV, Excel or PDF for compliance & audits.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-[60vh] overflow-y-auto">
          {reports.map(r => (
            <div key={r.kind} className="border border-slate-200 rounded-xl p-4" data-testid={`report-card-${r.kind}`}>
              <div className="flex items-start gap-2">
                <FileBarChart className="w-4 h-4 text-[#3D4A8E] mt-0.5" />
                <div className="flex-1">
                  <div className="font-semibold text-slate-900 text-sm">{r.label}</div>
                  <div className="text-[11px] text-slate-500 mt-0.5 font-mono">{r.kind}</div>
                </div>
              </div>
              <div className="flex gap-1.5 mt-3">
                <a href={buildUrl(r.kind, 'csv')}
                   download={`asset-${r.kind}.csv`}
                   className="inline-flex items-center justify-center gap-1 rounded-full h-8 text-xs flex-1 border border-slate-200 hover:bg-slate-50 px-3"
                   data-testid={`dl-${r.kind}-csv`}>
                  <FileText className="w-3 h-3" /> CSV
                </a>
                <a href={buildUrl(r.kind, 'xlsx')}
                   download={`asset-${r.kind}.xlsx`}
                   className="inline-flex items-center justify-center gap-1 rounded-full h-8 text-xs flex-1 border border-slate-200 hover:bg-slate-50 px-3"
                   data-testid={`dl-${r.kind}-xlsx`}>
                  <FileSpreadsheet className="w-3 h-3" /> Excel
                </a>
                <a href={buildUrl(r.kind, 'pdf')}
                   download={`asset-${r.kind}.pdf`}
                   className="inline-flex items-center justify-center gap-1 rounded-full h-8 text-xs flex-1 bg-[#3D4A8E] hover:bg-[#2F3A6E] text-white px-3"
                   data-testid={`dl-${r.kind}-pdf`}>
                  <Download className="w-3 h-3" /> PDF
                </a>
              </div>
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-full">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/* ---------- Transfer dialog (move asset between employees) ---------- */
function TransferDialog({ asset, onClose, onDone }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState(null);
  const [untilOffboarding, setUntilOffboarding] = useState(true);
  const [returnDate, setReturnDate] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const debounceRef = useRef(null);
  const abortRef = useRef(null);

  const currentHolder = asset?.current_assignment?.assigned_to_name || asset?.assigned_to_name;
  const currentHolderId = asset?.current_assignment?.assigned_to_id || asset?.assigned_to_id;

  const runSearch = (q) => {
    setQuery(q); setSelected(null);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (abortRef.current) abortRef.current.abort();
    if (q.trim().length < 2) { setResults([]); setSearching(false); return; }
    setSearching(true);
    const ctrl = new AbortController(); abortRef.current = ctrl;
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/assets/employee-search?q=${encodeURIComponent(q.trim())}`, { signal: ctrl.signal });
        // Exclude the current holder from results
        setResults((data || []).filter(e => e.id !== currentHolderId));
      } catch (e) { /* ignore */ }
      finally { setSearching(false); }
    }, 250);
  };

  const save = async () => {
    if (!selected) { toast.error('Pick a target employee'); return; }
    setBusy(true);
    try {
      await api.post(`/assets/${asset.id}/transfer`, {
        employee_id: selected.id,
        until_offboarding: untilOffboarding,
        expected_return_date: untilOffboarding ? null : (returnDate || null),
        reason,
      });
      toast.success(`Transferred to ${selected.name}`);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Transfer failed'); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={!!asset} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="transfer-asset-dialog" className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">Transfer {asset?.name}</DialogTitle>
          <DialogDescription>
            <span className="font-mono">{asset?.asset_id}</span> · currently with <b>{currentHolder || 'unknown'}</b>
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold text-slate-600">Transfer to (search by name or Employee ID) *</label>
            <Input value={query} onChange={(e) => runSearch(e.target.value)}
                   placeholder="e.g. Rohit, LAN-2026-…"
                   className="rounded-xl mt-1" data-testid="transfer-employee-search" />
            {searching && <div className="text-xs text-slate-500 mt-1">Searching…</div>}
            {!selected && results.length > 0 && (
              <div className="border border-slate-200 rounded-xl mt-2 max-h-60 overflow-y-auto" data-testid="transfer-search-results">
                {results.map(e => (
                  <button key={e.id} onClick={() => { setSelected(e); setResults([]); setQuery(''); }}
                          className="w-full text-left px-3 py-2 border-b border-slate-100 last:border-0 hover:bg-slate-50"
                          data-testid={`transfer-result-${e.id}`}>
                    <div className="font-semibold text-slate-900 text-sm">{e.name}</div>
                    <div className="text-[11px] text-slate-500">
                      {e.employee_id && <span className="font-mono">{e.employee_id}</span>}
                      {e.department && <> · {e.department}</>}
                      {e.email && <> · {e.email}</>}
                    </div>
                  </button>
                ))}
              </div>
            )}
            {!selected && !searching && query.trim().length >= 2 && results.length === 0 && (
              <div className="text-xs text-slate-500 mt-1">No matching employees.</div>
            )}
          </div>

          {selected && (
            <div className="border border-emerald-200 bg-emerald-50 rounded-xl p-3 flex items-start gap-3" data-testid="transfer-selected-employee">
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-slate-900 text-sm">{selected.name}</div>
                <div className="text-[11px] text-slate-600">
                  {selected.employee_id && <span className="font-mono">{selected.employee_id}</span>}
                  {selected.department && <> · {selected.department}</>}
                </div>
              </div>
              <button onClick={() => setSelected(null)} className="text-slate-500 hover:text-rose-600" data-testid="clear-selected-transfer">
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          {selected && (
            <>
              <div className="border border-slate-200 rounded-xl p-3">
                <div className="text-xs font-semibold text-slate-600 mb-2">Return policy</div>
                <label className="flex items-center gap-2 cursor-pointer mb-2">
                  <input type="radio" checked={untilOffboarding} onChange={() => setUntilOffboarding(true)} data-testid="transfer-policy-until-offboarding" />
                  <span className="text-sm">Until employee resigns or is terminated</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="radio" checked={!untilOffboarding} onChange={() => setUntilOffboarding(false)} data-testid="transfer-policy-fixed-date" />
                  <span className="text-sm">Fixed return date</span>
                </label>
                {!untilOffboarding && (
                  <Input type="date" value={returnDate} onChange={(e) => setReturnDate(e.target.value)}
                         className="rounded-xl mt-2" data-testid="transfer-fixed-return-date" />
                )}
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600">Reason for transfer</label>
                <Input value={reason} onChange={(e) => setReason(e.target.value)}
                       placeholder="e.g. Team change, role swap…"
                       className="rounded-xl mt-1" data-testid="transfer-reason" />
              </div>
            </>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-full">Cancel</Button>
          <Button onClick={save} disabled={busy || !selected || (!untilOffboarding && !returnDate)} className="rounded-full bg-indigo-600 hover:bg-indigo-700" data-testid="confirm-transfer-asset">
            {busy ? 'Transferring…' : 'Confirm transfer'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
